import { useState } from 'react';
import { ChevronRight, ArrowUpDown } from 'lucide-react';
import { IUserERC20Balance } from '@shared/types/frontend/assetPage';
import { TradeAssetsPageSchemaInputs } from '@shared/zod-schemas/frontend';
import { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import ImageWithBadge from '@/components/asset/sendFunds/ImageWithBadge';
import { cn } from '@/lib/misc/utils';
import SelectToTokenListModalContent from './modals/SelectToTokenListModalContent';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useSession } from '@/contexts/LitSessionProvider';
import PortfolioBalancesModalContent from './modals/PortfolioBalancesModalContent';
interface SelectFromToAssetProps {
  form: UseFormReturn<TradeAssetsPageSchemaInputs>;
  assetData: IUserERC20Balance;
  handleOnSwitchTokenSides: () => void;
}
const SelectFromToTrade = ({
  form,
  assetData,
  handleOnSwitchTokenSides
}: SelectFromToAssetProps) => {
  const {
    PKP
  } = useSession();
  const [isFromTokenModalOpen, setIsFromTokenModalOpen] = useState(false);
  const [isToTokenModalOpen, setIsToTokenModalOpen] = useState(false);
  const _handleFromTokenToggleModal = () => {
    setIsFromTokenModalOpen(!isFromTokenModalOpen);
    return;
  };
  const _handleToTokenToggleModal = () => {
    setIsToTokenModalOpen(!isToTokenModalOpen);
    return;
  };
  const disabled = !PKP || !assetData;
  return <div className='grid relative genius-shadow' data-sentry-component="SelectFromToTrade" data-sentry-source-file="SelectFromToTrade.tsx">
      {/* from */}
      <Dialog open={isFromTokenModalOpen} onOpenChange={_handleFromTokenToggleModal} data-sentry-element="Dialog" data-sentry-source-file="SelectFromToTrade.tsx">
        <DialogTrigger className='w-full' asChild data-sentry-element="DialogTrigger" data-sentry-source-file="SelectFromToTrade.tsx">
          <Button {...!disabled && {
          disabled
        }} type='button' variant='ghost' className={cn('w-full justify-between hover:bg-genius-blue p-8 bg-genius-blue rounded-none rounded-t-3xl', form.watch('fromToken') ? 'bg-genius-indigo border border-genius-blue' : '')} onClick={() => _handleFromTokenToggleModal()} data-sentry-element="Button" data-sentry-source-file="SelectFromToTrade.tsx">
            <>
              {form.watch('fromToken') ? <>
                  <div className='flex gap-2 items-center'>
                    <ImageWithBadge mainImageSrc={form.watch('fromToken').logo_url} badgeImageSrc={getNetworkLogo(form.watch('fromToken').network)} size={32} />
                    <div className='grid gap-1 justify-items-start'>
                      <p className='text-xs opacity-50'>From</p>
                      <p>{form.watch('fromToken').contract_ticker_symbol}</p>
                    </div>
                  </div>
                  <ChevronRight />
                </> : <>
                  <div className='flex gap-4'>
                    <p className='opacity-50 w-12 text-left'>From:</p>
                    <p>Select</p>
                  </div>
                  <ChevronRight />
                </>}
            </>
          </Button>
        </DialogTrigger>
        <DialogContent hideClose className={`dialog-content max-h-[755px] max-w-[834px] w-full h-full overflow-hidden p-4 rounded-[20px]`} data-sentry-element="DialogContent" data-sentry-source-file="SelectFromToTrade.tsx">
          <DialogTitle className='hidden' data-sentry-element="DialogTitle" data-sentry-source-file="SelectFromToTrade.tsx" />
          <div className='w-full justify-center'>
            <PortfolioBalancesModalContent assetData={assetData} form={form} handleToggleModal={_handleFromTokenToggleModal} data-sentry-element="PortfolioBalancesModalContent" data-sentry-source-file="SelectFromToTrade.tsx" />
          </div>
        </DialogContent>
      </Dialog>

      {/* switch button */}
      <Button type='button' disabled={!assetData?.balances.some(token => token.contract_address.toLocaleLowerCase() === form.watch('toToken')?.contract_address.toLocaleLowerCase() && token?.usd_value > 1)} onClick={() => handleOnSwitchTokenSides()} variant='ghost' size='icon' className={cn('absolute bg-genius-indigo place-self-center border border-genius-blue', assetData?.balances.some(token => token.contract_address.toLocaleLowerCase() === form.watch('toToken')?.contract_address.toLocaleLowerCase() && token?.usd_value > 1) ? '' : 'hidden')} data-sentry-element="Button" data-sentry-source-file="SelectFromToTrade.tsx">
        <ArrowUpDown className='w-4 h-4' data-sentry-element="ArrowUpDown" data-sentry-source-file="SelectFromToTrade.tsx" />
      </Button>

      {/* to */}

      <Dialog open={isToTokenModalOpen} onOpenChange={_handleToTokenToggleModal} data-sentry-element="Dialog" data-sentry-source-file="SelectFromToTrade.tsx">
        <DialogTrigger className='w-full' asChild data-sentry-element="DialogTrigger" data-sentry-source-file="SelectFromToTrade.tsx">
          <Button {...!disabled && {
          disabled
        }} type='button' variant='ghost' className={cn('w-full justify-between hover:bg-genius-blue p-8 bg-genius-blue rounded-none rounded-b-3xl', form.watch('toToken') ? 'bg-genius-indigo border border-t-0 border-genius-blue' : '')} onClick={() => _handleToTokenToggleModal()} data-sentry-element="Button" data-sentry-source-file="SelectFromToTrade.tsx">
            <>
              {form.watch('toToken') ? <>
                  <div className='flex gap-2 items-center'>
                    <ImageWithBadge mainImageSrc={form.watch('toToken').logo_url} badgeImageSrc={getNetworkLogo(form.watch('toToken').network)} size={32} />
                    <div className='grid gap-1 justify-items-start'>
                      <p className='text-xs opacity-50'>To</p>
                      <p>{form.watch('toToken').contract_ticker_symbol}</p>
                    </div>
                  </div>
                  <ChevronRight />
                </> : <>
                  <div className='flex gap-4'>
                    <p className='opacity-50 w-12 text-left'>To:</p>
                    <p>Select</p>
                  </div>
                  <ChevronRight />
                </>}
            </>
          </Button>
        </DialogTrigger>
        <DialogContent hideClose className={`dialog-content max-h-[755px] max-w-[834px] w-full h-full overflow-hidden p-4 rounded-[20px]`} data-sentry-element="DialogContent" data-sentry-source-file="SelectFromToTrade.tsx">
          <DialogTitle className='hidden' data-sentry-element="DialogTitle" data-sentry-source-file="SelectFromToTrade.tsx" />
          <div className='w-full justify-center'>
            <SelectToTokenListModalContent form={form} handleToggleModal={_handleToTokenToggleModal} data-sentry-element="SelectToTokenListModalContent" data-sentry-source-file="SelectFromToTrade.tsx" />
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};
export default SelectFromToTrade;